import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { AppMenu, Flex, Text } from '@ghq-abi/design-system';

import { useNav } from '~/app/contexts/NavContext';
import { BackArrowIcon } from '~/shared/components/icons';

import { useMenuListScroll } from '../../hooks/useMenuListScroll';
import { useNavAnalytics } from '../../hooks/useNavAnalytics';

export function DesktopMenu() {
  const {
    isAppMenuOpen,
    subMenuOpen,
    menuHeaderItem,
    menuItems,
    footerItems,
    toggleAppMenu,
    checkIfIsActive,
    toggleSubMenu,
  } = useNav();

  const { elementRef, isScrollable } = useMenuListScroll();
  const { fireMenuItemClick } = useNavAnalytics();

  return (
    <AppMenu open={isAppMenuOpen}>
      <AppMenu.Trigger asChild>
        <AppMenu.Bar openMenu={toggleAppMenu} openMenuDelay={300}>
          <AppMenu.Header css={{ p: 0 }}>
            <AppMenu.MenuItem
              css={{ p: '$3 $2' }}
              leftIcon={menuHeaderItem.icon}
              isTriggerItem
            />
          </AppMenu.Header>
          <AppMenu.MenuList>
            {menuItems.map(item => (
              <AppMenu.MenuItem
                key={item.key}
                leftIcon={item.icon}
                isActive={checkIfIsActive(item.path, item.routeComponents)}
                isTriggerItem
              />
            ))}
          </AppMenu.MenuList>
          <AppMenu.Footer>
            {footerItems.map(item => (
              <AppMenu.MenuItem
                key={item.key}
                leftIcon={item.icon}
                isActive={checkIfIsActive(item.path, item.routeComponents)}
                isTriggerItem
              />
            ))}
          </AppMenu.Footer>
        </AppMenu.Bar>
      </AppMenu.Trigger>
      <AppMenu.Content
        closeMenuDelay={300}
        closeMenu={() => {
          toggleAppMenu();
          toggleSubMenu(null);
        }}
      >
        <AppMenu.Header css={{ p: 0 }}>
          <AppMenu.MenuItem
            title={menuHeaderItem.title}
            hasSubmenu
            css={{ p: '$3 $2' }}
            leftIcon={menuHeaderItem.icon}
            isSubMenuOpen={subMenuOpen?.some(
              menu => menu?.key === menuHeaderItem.key
            )}
            onClick={() => toggleSubMenu(menuHeaderItem)}
            renderSubmenu={
              <AppMenu.SubMenu
                title={menuHeaderItem.title || ''}
                alignWithHeader
                hideTrigger
                renderTitle={
                  <Flex align="center" gap="md" css={{ height: '$full' }}>
                    <AppMenu.MenuIconButton
                      onClick={() => toggleSubMenu(null)}
                      icon={<BackArrowIcon />}
                    />
                    <Text css={{ fontWeight: '$medium' }}>
                      {menuHeaderItem.title}
                    </Text>
                  </Flex>
                }
              >
                <AppMenu.MenuList>
                  {menuHeaderItem.routeComponents?.map(subitem => (
                    <AppMenu.MenuItem
                      key={subitem.key}
                      title={subitem.title}
                      route={subitem.path}
                      leftIcon={subitem.icon}
                      isSubMenuItem
                    />
                  ))}
                </AppMenu.MenuList>
              </AppMenu.SubMenu>
            }
          />
        </AppMenu.Header>
        <AppMenu.MenuList
          isMainList
          ref={elementRef}
          bottomElement={
            <Image
              src="/img/cheers_menu_logo.png"
              width={200}
              height={100}
              alt="Cheers Hub"
            />
          }
          bottomElementCss={{
            width: isScrollable
              ? 'calc($appMenuOpenWidth - $2)'
              : '$appMenuOpenWidth',
          }}
        >
          {menuItems.map(item => (
            <AppMenu.MenuItem
              key={item.key}
              route={item.path}
              title={item.title}
              leftIcon={item.icon}
              as={Link}
              hasSubmenu={!!item.routeComponents?.length}
              isSubMenuOpen={subMenuOpen?.some(menu => menu?.key === item.key)}
              onHover={() => toggleSubMenu(item, true)}
              onHoverDelay={100}
              isActive={checkIfIsActive(item.path, item.routeComponents)}
              {...(!item.routeComponents?.length && {
                onClick: () => fireMenuItemClick(item),
              })}
              renderSubmenu={
                <AppMenu.SubMenu
                  title={item.title || ''}
                  css={{ borderRadius: '$4' }}
                >
                  <AppMenu.MenuList>
                    {item.routeComponents?.map(subitem => (
                      <AppMenu.MenuItem
                        key={subitem.key}
                        title={subitem.title}
                        route={subitem.path}
                        as={Link}
                        hasSubmenu={!!subitem.routeComponents?.length}
                        isSubMenuOpen={subMenuOpen?.some(
                          menu => menu?.key === subitem.key
                        )}
                        onHover={() => {
                          toggleSubMenu(subitem, false);
                        }}
                        onHoverDelay={300}
                        isActive={checkIfIsActive(
                          subitem.path,
                          subitem.routeComponents
                        )}
                        {...(!subitem.routeComponents?.length && {
                          onClick: () => fireMenuItemClick(subitem),
                        })}
                        renderSubmenu={
                          <AppMenu.SubMenu
                            title={subitem.title || ''}
                            css={{ borderRadius: '$4' }}
                          >
                            <AppMenu.MenuList>
                              {subitem.routeComponents?.map(otherItem => (
                                <AppMenu.MenuItem
                                  key={otherItem.key}
                                  title={otherItem.title}
                                  route={otherItem.path}
                                  as={Link}
                                  onClick={() => fireMenuItemClick(otherItem)}
                                />
                              ))}
                            </AppMenu.MenuList>
                          </AppMenu.SubMenu>
                        }
                      />
                    ))}
                  </AppMenu.MenuList>
                </AppMenu.SubMenu>
              }
            />
          ))}
        </AppMenu.MenuList>
        <AppMenu.Footer>
          {footerItems.map(item => (
            <AppMenu.MenuItem
              key={item.key}
              route={item.path}
              title={item.title}
              hasSubmenu={!!item.routeComponents?.length}
              leftIcon={item.icon}
              isSubMenuOpen={subMenuOpen?.some(menu => menu?.key === item.key)}
              onHover={() => toggleSubMenu(item)}
              onHoverDelay={300}
              isActive={checkIfIsActive(item.path, item.routeComponents)}
              {...(!item.routeComponents?.length && {
                onClick: () => fireMenuItemClick(item),
              })}
              renderSubmenu={
                <AppMenu.SubMenu
                  title={item.title || ''}
                  css={{ borderRadius: '$4' }}
                >
                  <AppMenu.MenuList>
                    {item.routeComponents?.map(subitem => (
                      <AppMenu.MenuItem
                        key={subitem.key}
                        title={subitem.title}
                        route={subitem.path}
                        isSubMenuItem
                        onClick={() => fireMenuItemClick(subitem)}
                      />
                    ))}
                  </AppMenu.MenuList>
                </AppMenu.SubMenu>
              }
            />
          ))}
        </AppMenu.Footer>
      </AppMenu.Content>
    </AppMenu>
  );
}
